import { translatedClientError } from "../i18n/client-error.js";
import { validatedDisplayView } from "../i18n/display-view.js";
import { portalLocale, portalText } from "../i18n/portal.js";
import { isServerFault, SERVER_FAULT_COPY } from "./server-fault.js";
import { translatedApiMessage } from "../i18n/api-message.js";
/**
 * The console's reading of a refused API call: which class of failure it is, and the sentence a
 * screen shows for it.
 *
 * ── THE CLASSES ──────────────────────────────────────────────────────────────────────────
 * One per HTTP status the API answers with, named after design §9.2's taxonomy
 * (`packages/domain/src/error/taxonomy.ts`). Since 2026-09-23 (register 0z.7) every status the API
 * sends has a class there, so the third column names a class on every row:
 *
 * | Status  | Class                  | Taxonomy class                                              | Retry |
 * |---------|------------------------|-------------------------------------------------------------|-------|
 * | 400     | `validation`           | `ValidationError`                                           | never |
 * | 401     | `authentication`       | `AuthenticationError`                                       | never |
 * | 402     | `insufficient_balance` | `PaymentRequiredError` — the live-balance refusals          | after paying |
 * |         |                        | `credit_exhausted`, `trial_exhausted`, `hard_stop_reached`,  |       |
 * |         |                        | and the workspace budget's `budget_exceeded` (2026-09-23)    |       |
 * | 403     | `authorization`        | `AuthorizationError`                                        | never |
 * | 404     | `not_found`            | `NotFoundError`                                             | never |
 * | 409     | `conflict`             | `ConflictError`                                             | after refetch |
 * | 410     | `gone`                 | `GoneError` — `upload_grant_expired` (design §16.18)        | never |
 * | 413     | `body_too_large`       | `PayloadTooLargeError` — `payload_too_large` from the upload | never unchanged |
 * |         |                        | routes and `serve.ts`'s body cap, or an HTML page from the   |       |
 * |         |                        | proxy                                                        |       |
 * | 415     | `unsupported_type`     | `UnsupportedMediaTypeError` — `document_type_unsupported`   | never |
 * | 422     | `unprocessable`        | `UnprocessableError`, `LanguageUnavailableError`            | never |
 * | 429     | `rate_limited`         | the pipeline's limiter                                      | later |
 * | 500     | `internal`             | anything unclassified                                       | never |
 * | 502/504 | `upstream`             | `UpstreamError`                                             | later (bounded) |
 * | 503     | `not_ready`            | `ServiceUnavailableError` — `agent_runtime_unavailable`, a  | later |
 * |         |                        | draining or not-ready process, or the ingress with no ready  |       |
 * |         |                        | replica                                                      |       |
 *
 * `api-error.test.ts` pins the Retry column to each class's `retryable`, so the two cannot drift.
 *
 * Anything else is `unexpected` — named as such rather than filed under a plausible neighbour.
 *
 * `budget_exceeded` is the one 402 that is not about the account's credit (admission's reason is
 * `budget_reached`; the API answers it with the documented error code): THIS workspace's monthly
 * budget has its hard stop on and is spent, while the account may have credit left and its other
 * workspaces keep answering. Its remedy is raising the budget (Billing → Spend controls), not a
 * top-up, which is why the class copy below names both — the API's own message, which says which
 * one fired, wins whenever it came back.
 *
 * ── WHOSE WORDS ──────────────────────────────────────────────────────────────────────────
 * When the API answered with its error envelope, its `message` is the sentence: the API wrote it
 * about the exact refusal, and the console does not paraphrase it. The class copy below is only
 * for a refusal that carries NO envelope — a 413 from nginx's body limit, a 503 from the ingress
 * while no replica is ready, a 502 from the load balancer — which `ApiError` otherwise renders as
 * the bare "HTTP 503".
 */
import { ApiError } from "@wetalk/api-client";

export type ApiErrorClassView =
  | "validation"
  | "authentication"
  | "insufficient_balance"
  | "authorization"
  | "not_found"
  | "conflict"
  | "gone"
  | "body_too_large"
  | "unsupported_type"
  | "unprocessable"
  | "rate_limited"
  | "internal"
  | "upstream"
  | "not_ready"
  | "unexpected";

/** When the same call may be made again, per the taxonomy's "Retryable" column. */
export type ApiErrorRetryView = "never" | "after_paying" | "after_refetch" | "later";

interface ApiErrorClassDefinition {
  readonly retry: ApiErrorRetryView;
  /** The sentence for a refusal that arrived without the API's envelope. */
  readonly text: string;
}

const BY_STATUS: ReadonlyMap<number, ApiErrorClassView> = new Map<number, ApiErrorClassView>([
  [400, "validation"],
  [401, "authentication"],
  [402, "insufficient_balance"],
  [403, "authorization"],
  [404, "not_found"],
  [409, "conflict"],
  [410, "gone"],
  [413, "body_too_large"],
  [415, "unsupported_type"],
  [422, "unprocessable"],
  [429, "rate_limited"],
  [500, "internal"],
  [502, "upstream"],
  [503, "not_ready"],
  [504, "upstream"],
]);

export const API_ERROR_CLASS: Readonly<Record<ApiErrorClassView, ApiErrorClassDefinition>> = {
  validation: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.validation"); } }),
  authentication: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.authentication"); } }),
  insufficient_balance: validatedDisplayView({
    retry: "after_paying",
    get text() { return portalText("shell", "errors.class.insufficient_balance"); },
  }),
  authorization: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.authorization"); } }),
  not_found: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.not_found"); } }),
  conflict: validatedDisplayView({
    retry: "after_refetch",
    get text() { return portalText("shell", "errors.class.conflict"); },
  }),
  gone: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.gone"); } }),
  body_too_large: validatedDisplayView({
    retry: "never",
    get text() { return portalText("shell", "errors.class.body_too_large"); },
  }),
  unsupported_type: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.unsupported_type"); } }),
  unprocessable: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.unprocessable"); } }),
  rate_limited: validatedDisplayView({ retry: "later", get text() { return portalText("shell", "errors.class.rate_limited"); } }),
  internal: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.internal"); } }),
  upstream: validatedDisplayView({ retry: "later", get text() { return portalText("shell", "errors.class.upstream"); } }),
  not_ready: validatedDisplayView({
    retry: "later",
    get text() { return portalText("shell", "errors.class.not_ready"); },
  }),
  unexpected: validatedDisplayView({ retry: "never", get text() { return portalText("shell", "errors.class.unexpected"); } }),
};

/** The class of a refused call, from its status alone. */
export function apiErrorClassOf(error: ApiError): ApiErrorClassView {
  return BY_STATUS.get(error.status) ?? "unexpected";
}

/**
 * Whether the API's own envelope came back. `ApiError` falls back to the message `HTTP <status>`
 * exactly when the body carried no `error.message`, which is the one case the class copy exists for.
 */
export function carriesEnvelope(error: ApiError): boolean {
  return error.message !== `HTTP ${String(error.status)}`;
}

/**
 * The sentence a screen shows for a rejected promise: the API's own message when it sent one, the
 * class's copy when it did not, an `Error`'s message otherwise, and `otherwise` for a rejection
 * that is not an `Error` at all.
 */
export function apiErrorText(error: unknown, otherwise: string): string {
  if (error instanceof ApiError) {
    if (!carriesEnvelope(error)) return API_ERROR_CLASS[apiErrorClassOf(error)].text;
    if (portalLocale() === "en") return error.message;
    if (isServerFault(error.status, error.code === "unknown_code" ? null : error.code)) return SERVER_FAULT_COPY();
    return translatedApiMessage(error.message) ?? portalText("shell", "errors.diagnostic", { code: error.code, message: error.message });
  }
  if (!(error instanceof Error)) return otherwise;
  const clientCopy = translatedClientError(error.message);
  if (clientCopy !== null) return clientCopy;
  if (portalLocale() === "en") return error.message;
  return translatedApiMessage(error.message) ?? portalText("shell", "errors.original", { message: error.message });
}

/**
 * {@link apiErrorText}, plus the request id when WeTalk itself failed (5xx). The API's own sentence
 * then says "Quote the request id when reporting this.", so a screen that shows it must show the
 * id too (customer-portal review 2026-09-25 #144, #150) — never a request to quote something the
 * reader cannot see.
 */
export function apiProblemText(error: unknown, otherwise: string): string {
  const text = apiErrorText(error, otherwise);
  if (error instanceof ApiError && error.status >= 500 && error.requestId !== null) {
    return `${text} ${portalText("shell", "errors.requestId", { requestId: error.requestId })}`;
  }
  return text;
}
